/**
 * Saved views of a list (docs/list-standard.md §8.1) as data and pure functions: what a view keeps
 * of the list in front of the person, and how a kept view is opened again on a list that may have
 * changed since. identity stores them (`/me/views`); nothing here asks anybody.
 *
 * A view keeps a tab by its value, never its position, and keeps only what the list declares: a
 * column the list no longer sorts or filters by is dropped when the view opens, and the list opens
 * with the rest — a view is never a reason for a list to refuse to draw.
 */
import { ALL, CLOSED, NO_WHERE, openingView, pruned } from "./derive.js";
import { isApplicable, isQuick } from "./manifest.js";
/** What the person has in front of them, as a view keeps it. Empty parts are left out. */
export function specOf(manifest, view, look) {
    const spec = {};
    const tabs = manifest.tabs;
    if (view.tab === CLOSED)
        spec.tab = 'closed';
    else if (view.tab !== ALL && tabs?.values && view.tab >= 0 && view.tab < tabs.values.length)
        spec.tab = String(tabs.values[view.tab]);
    if (view.search.trim())
        spec.search = view.search;
    const filters = Object.fromEntries(Object.entries(view.filters).filter(([, v]) => v));
    if (Object.keys(filters).length)
        spec.filters = filters;
    const where = pruned(view.where);
    if (where.items.length)
        spec.where = where;
    if (view.sort && view.sort !== (manifest.sort?.default ?? ''))
        spec.sort = view.sort;
    if (view.size !== undefined && view.size !== manifest.page?.size)
        spec.size = view.size;
    if (look.view && look.view !== 'table' && look.view !== 'board')
        spec.layout = look.view;
    else if (manifest.board || manifest.calendar)
        spec.layout = look.board ? 'board' : 'table';
    if (look.allColumns)
        spec.columns = 'all';
    return spec;
}
/** The tab a kept value names on this list: its index, CLOSED, or null when the list has no such tab (yet). */
export function tabIndexOf(manifest, value) {
    const tabs = manifest.tabs;
    if (!tabs)
        return null;
    if (value === 'closed')
        return tabs.closed?.length ? CLOSED : null;
    const index = (tabs.values ?? []).findIndex((v) => String(v) === value);
    return index >= 0 ? index : null;
}
/**
 * A kept view opened on the list as it is now. What the list still declares is kept: a quick filter
 * on a column it still offers, a condition on a column it still filters with that operator, a sort
 * on a column it still sorts by, a page size it still offers, the board when it still has one.
 */
export function viewOfSpec(manifest, spec) {
    const opening = openingView(manifest);
    const view = { ...opening, where: NO_WHERE };
    let pendingTab;
    if (spec.tab) {
        const index = tabIndexOf(manifest, spec.tab);
        if (index !== null)
            view.tab = index;
        else if (manifest.tabs?.values_from && !manifest.tabs.values)
            pendingTab = spec.tab;
    }
    if (spec.search && manifest.search)
        view.search = spec.search;
    const quick = new Set((manifest.filters ?? []).filter(isQuick).map((f) => f.column));
    view.filters = Object.fromEntries(Object.entries(spec.filters ?? {}).filter(([k, v]) => quick.has(k) && v));
    if (spec.where)
        view.where = keptWhere(manifest, spec.where);
    if (spec.sort && (manifest.sort?.columns ?? []).includes(spec.sort.replace(/^-/, '')))
        view.sort = spec.sort;
    if (spec.size && manifest.page && (manifest.page.sizes ?? [10, 25, 50, 100]).includes(spec.size))
        view.size = spec.size;
    const look = {};
    if (spec.layout) {
        look.board = spec.layout === 'board' && Boolean(manifest.board);
        // A calendar or a timeline the list still declares; otherwise the table, as for a board that is gone.
        if (spec.layout === 'calendar' || spec.layout === 'timeline')
            look.view = manifest[spec.layout] ? spec.layout : 'table';
    }
    if (spec.columns)
        look.allColumns = spec.columns === 'all';
    return { view, look, ...(pendingTab !== undefined ? { pendingTab } : {}) };
}
function keptWhere(manifest, where) {
    // The loader already refused an operator a column's type cannot compare with (inspect), so the
    // filter's own list is the list.
    const allowed = new Map();
    for (const f of manifest.filters ?? [])
        if (f.operators?.length && isApplicable(f))
            allowed.set(f.column, f.operators);
    const keep = (node) => {
        if ('items' in node) {
            const items = node.items.map(keep).filter((x) => x !== null);
            return items.length ? { rel: node.rel === 'any' ? 'any' : 'all', items } : null;
        }
        const c = node;
        return allowed.get(c.column)?.includes(c.op) ? c : null;
    };
    return pruned(keep(where) ?? NO_WHERE);
}
/** Whether two kept views say the same thing — the list in front of the person still is the saved view. */
export function sameSpec(a, b) {
    return canonical(a) === canonical(b);
}
function canonical(x) {
    if (Array.isArray(x))
        return `[${x.map(canonical).join(',')}]`;
    if (x && typeof x === 'object') {
        return `{${Object.keys(x).sort().filter((k) => x[k] !== undefined)
            .map((k) => `${JSON.stringify(k)}:${canonical(x[k])}`).join(',')}}`;
    }
    return JSON.stringify(x);
}
/** A view as identity answers it, or null when it is not one. */
export function readSavedView(x) {
    if (!x || typeof x !== 'object')
        return null;
    const v = x;
    if (typeof v['id'] !== 'string' || typeof v['name'] !== 'string' || typeof v['version'] !== 'number' || !v['spec'] || typeof v['spec'] !== 'object')
        return null;
    return {
        id: v['id'], name: v['name'], version: v['version'], spec: v['spec'],
        shared: v['shared'] === true, mine: v['mine'] === true, editable: v['editable'] === true,
        owner_name: typeof v['owner_name'] === 'string' ? v['owner_name'] : '',
    };
}
//# sourceMappingURL=saved-views.js.map